/**
 * PSV-1726 — ship class picker modal.
 *
 * Opened by the UpgradePanel's Upgrade button (ArenaController wires the
 * click through `openClassPicker()`). Renders the `nextChoicesFor(...)`
 * cards side by side, centered on screen; each card shows the class name,
 * flavor line, stat block, and gem cost. A card is clickable only when the
 * player can afford it (dimmed otherwise); clicking an affordable card
 * fires `onPick(classId)` and closes the modal. Clicking anywhere outside
 * the cards (the dark backdrop) closes without picking.
 *
 * Structure mirrors UpgradePanel (Phaser container + graphics + text, no
 * DOM), including the container-level `setInteractive` pattern from
 * PSV-1338 Bug 1 Pass 3: the CONTAINER captures every pointerdown across
 * the full screen and the handler decides card-hit vs backdrop-close from
 * the container-local coordinates. The container sits at (0, 0) with
 * scrollFactor 0, so local coords == screen coords.
 *
 * The modal is rebuilt from scratch on every `show()` and torn down on
 * `hide()` — choices/gems change between openings, so there is no state
 * worth keeping alive while hidden.
 */

import Phaser from 'phaser';

import type { ShipClassDef } from '../../../server/shared/shipClasses';

// Card geometry — exported so tests can compute hit coordinates without
// mirroring magic numbers (same spirit as UpgradePanel's BUTTON_LOCAL_*).
export const CARD_WIDTH = 240;
export const CARD_HEIGHT = 240;
export const CARD_GAP = 24;

const BACKDROP_COLOR = 0x000000;
const BACKDROP_ALPHA = 0.6;
const CARD_BG_COLOR = 0x101521;
const CARD_BG_ALPHA = 0.95;
const CARD_BORDER_AFFORDABLE = 0x44ff7a;
const CARD_BORDER_DIM = 0x3a4768;
const DIMMED_ALPHA = 0.4;
const COLOR_TEXT = '#eeeeee';
const COLOR_TEXT_DIM = '#9aa0b4';
const COLOR_COST_OK = '#44ff7a';
const COLOR_COST_SHORT = '#ff5577';
// Above every other HUD element (UpgradePanel depth 18, toasts depth 25).
const MODAL_DEPTH = 30;

export type ClassPickHandler = (classId: string) => void;

/** Container-local hit rect + affordability for one rendered card. */
interface CardHit {
  classId: string;
  x: number;
  y: number;
  affordable: boolean;
}

export class ClassPickerModal {
  private readonly scene: Phaser.Scene;
  private readonly onPick: ClassPickHandler;

  private container: Phaser.GameObjects.Container | null = null;
  private cards: CardHit[] = [];
  private destroyed = false;
  /**
   * PSV-1902 QA — two-step confirm: a 300-gem tier-up fired off a single
   * (sometimes accidental) click. First click on a card ARMS it (gold
   * border + CLICK AGAIN TO CONFIRM), the second click within the same
   * open modal buys. Any other click re-arms/backdrop-closes as before.
   */
  private armedClassId: string | null = null;
  private confirmText: Phaser.GameObjects.Text | null = null;

  constructor(scene: Phaser.Scene, onPick: ClassPickHandler) {
    this.scene = scene;
    this.onPick = onPick;
  }

  /** Build + display the modal. Re-entrant: an already-open modal is rebuilt. */
  show(choices: ShipClassDef[], gems: number): void {
    if (this.destroyed || choices.length === 0) return;
    this.hide();
    this.armedClassId = null;

    const cam = this.scene.cameras.main;
    this.container = this.scene.add
      .container(0, 0)
      .setScrollFactor(0)
      .setDepth(MODAL_DEPTH);

    // Full-screen dim backdrop — also the click-outside close target.
    const backdrop = this.scene.add.graphics();
    backdrop.fillStyle(BACKDROP_COLOR, BACKDROP_ALPHA);
    backdrop.fillRect(0, 0, cam.width, cam.height);
    this.container.add(backdrop);

    const totalWidth =
      choices.length * CARD_WIDTH + (choices.length - 1) * CARD_GAP;
    const originX = cam.centerX - totalWidth / 2;
    const originY = cam.centerY - CARD_HEIGHT / 2;

    const title = this.scene.add
      .text(cam.centerX, originY - 40, 'Choose your ship class', {
        fontSize: '20px',
        color: COLOR_TEXT,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.container.add(title);

    this.cards = [];
    choices.forEach((def, i) => {
      const x = originX + i * (CARD_WIDTH + CARD_GAP);
      this.buildCard(def, x, originY, gems >= def.cost);
    });

    // Container-level interactive over the whole screen (see file docblock).
    this.container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, cam.width, cam.height),
      Phaser.Geom.Rectangle.Contains,
    );
    this.container.input!.cursor = 'pointer';
    this.container.on(
      'pointerdown',
      (
        _pointer: Phaser.Input.Pointer,
        localX: number,
        localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        // Same rationale as UpgradePanel: keep the scene-level fire
        // handler from ALSO discharging a shot on this click.
        event.stopPropagation();
        this.handlePointerDown(localX, localY);
      },
      this,
    );
  }

  hide(): void {
    this.cards = [];
    this.armedClassId = null;
    this.confirmText = null; // destroyed with the container
    if (this.container !== null) {
      this.container.destroy(true);
      this.container = null;
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.hide();
  }

  // ---------- private ----------

  private handlePointerDown(localX: number, localY: number): void {
    for (const card of this.cards) {
      const inside =
        localX >= card.x &&
        localX <= card.x + CARD_WIDTH &&
        localY >= card.y &&
        localY <= card.y + CARD_HEIGHT;
      if (!inside) continue;
      // Unaffordable card — swallow the click, keep the modal open so the
      // player can still read the stats / pick the other card.
      if (!card.affordable) return;
      if (this.armedClassId !== card.classId) {
        this.armClassCard(card);
        return;
      }
      const picked = card.classId;
      this.hide();
      this.onPick(picked);
      return;
    }
    // Backdrop click — close without picking.
    this.hide();
  }

  /** First-click arm: gold highlight + confirm hint under the card. */
  private armClassCard(card: CardHit): void {
    if (this.container === null) return;
    this.armedClassId = card.classId;
    this.confirmText?.destroy();
    this.confirmText = this.scene.add
      .text(card.x + CARD_WIDTH / 2, card.y + CARD_HEIGHT + 14, 'CLICK AGAIN TO CONFIRM', {
        fontSize: '13px',
        color: '#ffd84a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5);
    const glow = this.scene.add.graphics();
    glow.lineStyle(3, 0xffd84a, 1);
    glow.strokeRoundedRect(card.x - 2, card.y - 2, CARD_WIDTH + 4, CARD_HEIGHT + 4, 10);
    this.container.add([glow, this.confirmText]);
  }

  private buildCard(
    def: ShipClassDef,
    x: number,
    y: number,
    affordable: boolean,
  ): void {
    if (this.container === null) return;
    const alpha = affordable ? 1 : DIMMED_ALPHA;

    const bg = this.scene.add.graphics();
    bg.fillStyle(CARD_BG_COLOR, CARD_BG_ALPHA);
    bg.fillRect(x, y, CARD_WIDTH, CARD_HEIGHT);
    bg.lineStyle(2, affordable ? CARD_BORDER_AFFORDABLE : CARD_BORDER_DIM, 1);
    bg.strokeRect(x, y, CARD_WIDTH, CARD_HEIGHT);
    this.container.add(bg);

    const name = this.scene.add
      .text(x + CARD_WIDTH / 2, y + 14, def.name, {
        fontSize: '18px',
        color: COLOR_TEXT,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0)
      .setAlpha(alpha);
    this.container.add(name);

    const desc = this.scene.add
      .text(x + 12, y + 44, def.desc, {
        fontSize: '12px',
        color: COLOR_TEXT_DIM,
        wordWrap: { width: CARD_WIDTH - 24 },
      })
      .setAlpha(alpha);
    this.container.add(desc);

    const statLines = [
      `HP: ${def.hpMax}`,
      `Speed: ${def.engineMax}`,
      `Agility: ${def.agility}`,
      `Weapon: ${def.weapon.barrels}×${def.weapon.damage} dmg / ${def.weapon.reloadMs}ms`,
      `Cargo: ${def.cargoCapacity}`,
    ].join('\n');
    const stats = this.scene.add
      .text(x + 12, y + 92, statLines, {
        fontSize: '13px',
        color: COLOR_TEXT,
        lineSpacing: 4,
      })
      .setAlpha(alpha);
    this.container.add(stats);

    const cost = this.scene.add
      .text(x + CARD_WIDTH / 2, y + CARD_HEIGHT - 26, `Cost: ${def.cost} gems`, {
        fontSize: '14px',
        color: affordable ? COLOR_COST_OK : COLOR_COST_SHORT,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);
    this.container.add(cost);

    this.cards.push({ classId: def.id, x, y, affordable });
  }
}
